import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL, fetchWithAuth, getImageUrl } from '../api';
import QRCard from '../components/QRCard';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, Share2, Download, Clock, AlertCircle } from 'react-feather';

export default function QRPage() {
  const { eventId } = useParams(); // get from the url
  const navigate = useNavigate(); //
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [qrStatus, setQrStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  useEffect(() => {
    const fetchEventAndQR = async () => {
      try {
        const [eventData, qrResponse] = await Promise.all([
           fetchWithAuth(`${API_URL}/events/${eventId}`),
           fetchWithAuth(`${API_URL}/events/${eventId}/qr`)
        ]);
        setEvent(eventData);
        setQrStatus(qrResponse);
      } catch (err) {
        setError(err.message || 'Error al cargar el ticket');
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchEventAndQR();
  }, [eventId, user]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="fixed inset-0 z-[100] bg-black text-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <p className="text-lg font-medium mb-6">{error || 'Debes iniciar sesión'}</p>
          <button onClick={() => navigate(-1)} className="px-8 py-3 bg-white text-black rounded-full font-bold">Volver</button>
        </div>
      </div>
    );
  }

  // PENDING STATE
  if (qrStatus?.status === 'pending') {
    return (
      <div className="fixed inset-0 z-[100] bg-black text-white overflow-hidden font-sans">
        <div 
           className="absolute inset-0 z-0 opacity-50"
           style={{
             backgroundImage: `url(${getImageUrl(event.image_url)})`,
             backgroundSize: 'cover',
             backgroundPosition: 'center',
             filter: 'blur(30px) saturate(1.5)',
           }}
        />
        <div className="absolute inset-0 bg-black/60 z-0" />
        
        {/* Contenido centrado */}
        <div className="relative z-10 h-full flex flex-col">
           <div className="pt-safe-top px-6 py-4 mt-2"> {/* Margen superior para evitar choque */}
             <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md">
                <ChevronLeft size={24} />
             </button>
           </div>
           <div className="flex-1 flex flex-col items-center justify-center p-8 text-center animate-fade-in-up">
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6 border border-yellow-500/30 shadow-[0_0_30px_rgba(234,179,8,0.2)]">
                 <Clock size={32} className="text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Solicitud Enviada</h2>
              <p className="text-gray-300 text-sm max-w-xs mx-auto">Tu código QR aparecerá aquí pronto.</p>
           </div>
        </div>
      </div>
    );
  }

  // TICKET APPROVED STATE
  return (
    // FIX 2: z-[100] para estar por encima de CUALQUIER otra navegación de la app
    <div className="fixed inset-0 z-[100] bg-black text-white font-sans overflow-hidden">
      
      {/* Background Layers */}
      <div
        className="absolute inset-0 z-0 scale-110"
        style={{
          backgroundImage: `url(${getImageUrl(event.image_url)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(40px) brightness(0.6) saturate(1.5)',
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/40 via-transparent to-black/90" />

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col">
        
        {/* Header - FIX 2: Añadido mt-2 y fondo oscuro suave al botón para separarlo visualmente */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2 mt-safe-top">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-md border border-white/10 text-white hover:bg-white/20 transition-all active:scale-95"
          >
            <ChevronLeft size={24} />
          </button>
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-white/60">Ticket Digital</span>
          <div className="w-10" /> 
        </div>

        {/* Ticket Area - FIX 1: Centrado absoluto y restricción de ancho */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 pb-24 overflow-y-auto no-scrollbar">
          
          {/* Contenedor Restrictivo: max-w-[320px] fuerza la verticalidad */}
          <div className="w-full max-w-[350px] min-h-[500px] transform transition-all animate-fade-in-up duration-500">
            
            <div className="shadow-[0_20px_50px_rgba(0,0,0,0.5)] rounded-3xl overflow-hidden relative border border-white/10 bg-white/5 backdrop-blur-sm">
               {/* Shine effect */}
               <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent opacity-30 pointer-events-none z-20"/>
               
               <QRCard 
                  qrUrl={qrStatus?.qr_url}
                  eventName={event?.name}
                  date={event ? capitalizeFirst(format(parseISO(event.date), "EEEE d 'de' MMMM", { locale: es })) : ''}
                  hour={event ? event.time : ''}
                  userName={user?.name}
                  dni={user?.dni}
                  image_url={getImageUrl(event?.image_url)}
                />
            </div>

            {/* Helper Text */}
            <p className="text-center text-white/40 text-xs mt-6 animate-pulse tracking-wide">
              Brillo al máximo al escanear
            </p>

            {/* Action Buttons */}
            <div className="flex justify-center gap-6 mt-8">
               <button className="flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors group">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-white/10 transition-colors">
                     <Share2 size={20} />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-medium">Enviar</span>
               </button>
               <button className="flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors group">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-white/10 transition-colors">
                     <Download size={20} />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-medium">Guardar</span>
               </button>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}